const m = require('minthril');
const { component } = require('../../component');
const { CompatibilityLogos } = require('../../components/compatibility-logos');
const { navigate } = require('../../routes');

const HomePage = component((vnode, emit) => {
  let estimateDomain = '';

  function openPricingEstimate (event) {
    event.preventDefault();
    const input = estimateDomain.trim();
    const lowerCaseInput = input.toLowerCase();
    const hasProtocol = lowerCaseInput.startsWith('http://') || lowerCaseInput.startsWith('https://');
    const url = new URL(hasProtocol ? input : 'https://' + input);
    estimateDomain = url.hostname;
    navigate(`/docs/pricing?domain=${encodeURIComponent(estimateDomain.trim())}`);
  }

  return {
    view: ({ attrs: { user, onSignIn } }) => m('div.home-page',
      m('img.home-background-logo', {
        src: '/images/logo%20big.png',
        alt: '',
        'aria-hidden': 'true'
      }),
      m('section.home-hero',
        m('p.home-eyebrow', 'Keep your stack. Add server-rendered pages.'),
        m('h2', 'SSR for ANY website.'),
        m('h3.home-introduction',
          'Spache runs your app in a browser, caches the rendered HTML, and serves it to visitors and crawlers. ' +
          'No SSR framework or rewrite needed.'
        ),
        m('p.home-spached', [
          m('span.home-spached__mark', { 'aria-hidden': 'true' }, '✓'),
          m('span.home-spached__copy', [
            m('strong', 'We spent zero seconds making this crawlable, you still found us.'),
            m('span', ' This site is a plain JS SPA. Spache serves its rendered HTML to you and to crawlers.')
          ])
        ]),
      ),
      m('section.home-estimate', [
        m('div.home-estimate__copy', [
          m('p.home-eyebrow', 'Try Spache on your site'),
          m('h3', 'See what Spache can do for your site'),
          m('p', 'We’ll render your public homepage once, show you the result, and estimate 100, 1,000, and 10,000 renders a month.')
        ]),
        m('form.home-estimate__form', { onsubmit: openPricingEstimate }, [
          m('label', { for: 'home-estimate-domain' }, 'Your site'),
          m('div.home-estimate__controls', [
            m('input#home-estimate-domain', {
              type: 'text',
              inputmode: 'url',
              autocomplete: 'url',
              placeholder: 'example.com',
              pattern: '(?:https?://)?(?:[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\\.)+[a-zA-Z]{2,}(?:/[^\\s]*)?',
              title: 'Enter a domain such as example.com',
              required: true,
              value: estimateDomain,
              oninput: event => {
                estimateDomain = event.target.value;
                emit('redraw');
              }
            }),
            m('button.button', { type: 'submit' }, 'Test your site in Spache!')
          ]),
          m('span.home-estimate__note', 'Public homepage only')
        ])
      ]),
      m(CompatibilityLogos, {
        className: 'compatibility-logos--hero',
        description: 'Vanilla JavaScript, React, Vue, Angular, Svelte - anything that runs in a browser.'
      }),
      m('section.home-value',
        m('h3', 'SEO and page speed, without an SSR rewrite.'),
        m('p',
          'Search engines receive rendered page content instead of an empty app shell. Visitors can start reading ' +
          'that content while your app bundle loads and starts. Spache handles rendering and cache refresh; your ' +
          'SPA still handles interaction.'
        ),
        m('div.home-points',
          m('article',
            m('h4', 'No SSR rewrite'),
            m('p', 'Keep the SPA and framework you already use. You do not need to add server-rendering code to your app.')
          ),
          m('article',
            m('h4', 'No HTML publishing pipeline'),
            m('p', 'Spache renders routes, stores the HTML, and serves it. You do not need a separate build and deploy step for generated pages.')
          ),
          m('article',
            m('h4', 'Test before changing DNS'),
            m('p', 'Use a preview URL to check rendered routes and errors. Connect your domain when the pages look right.')
          )
        )
      ),
      m('section.home-performance-proof',
        m('div.home-performance-proof__copy',
          m('p.home-eyebrow', 'A real site, before and after Spache'),
          m('h3', 'Faster pages. Content search engines can read.'),
          m('p',
            'On Mid Engine Co, the Lighthouse performance score went from 89 to 98, while Largest Contentful Paint ' +
            'improved from 3.0 seconds to 2.1 seconds. Serving rendered pages sooner can improve the visitor ' +
            'experience and support SEO; Spache also gives crawlers the rendered page content directly.'
          )
        ),
        m('div.home-performance-proof__examples', [
          m('figure.home-performance-proof__example', [
            m('img', {
              src: '/images/midenginecobeforespache.png',
              alt: 'Mid Engine Co Lighthouse results before Spache: performance 89 and Largest Contentful Paint 3.0 seconds',
              loading: 'lazy'
            }),
            m('figcaption', 'Before Spache · Performance 89')
          ]),
          m('figure.home-performance-proof__example', [
            m('img', {
              src: '/images/midenginecoafterspache.png',
              alt: 'Mid Engine Co Lighthouse results with Spache: performance 98 and Largest Contentful Paint 2.1 seconds',
              loading: 'lazy'
            }),
            m('figcaption', 'With Spache · Performance 98')
          ])
        ])
      ),
      m('section.home-fit',
        m('h3', 'Less framework context. More product context.'),
        m('p', 'Human or agent, products move faster with less context to carry.'),
        m('p',
          'Your team or coding agent does not need to build and maintain server rendering, page caching, and refresh ' +
          'jobs in the app. Spache handles that work outside it, leaving more time and token budget for the product.'
        )
      ),
      m('section.home-flow',
        m('div.home-flow__intro',
          m('p.home-eyebrow', 'Inside the request flow'),
          m('h3', [
            'Keep your SPA architecture. ',
            m('span', 'Spache makes the finished application available as ordinary HTML to anything that can’t—or doesn’t want to—run your JavaScript.')
          ]),
          m('p', 'Spache runs your deployed app in a browser and caches the rendered HTML. You can serve rendered public pages without maintaining a separate server rendering path in your application.')
        ),
        m('div.home-flow__diagram', [
          m('i.home-flow__orb.home-flow__orb--one', { 'aria-hidden': 'true' }),
          m('i.home-flow__orb.home-flow__orb--two', { 'aria-hidden': 'true' }),
          m('i.home-flow__orb.home-flow__orb--three', { 'aria-hidden': 'true' }),
          m('i.home-flow__orb.home-flow__orb--four', { 'aria-hidden': 'true' }),
          m('article.home-flow__card.home-flow__card--visit',
            m('i.home-flow__icon.home-flow__icon--visitor', { 'aria-hidden': 'true' }),
            m('div.home-flow__card-content', [
              m('h4', 'A visitor hits your site'),
              m('p', 'A person or search engine opens a URL.')
            ])
          ),
          m('div.home-flow__arrow.home-flow__arrow--one', { 'aria-hidden': 'true' }, '→'),
          m('article.home-flow__card.home-flow__card--spassdaas',
            m('i.home-flow__icon.home-flow__icon--shield', { 'aria-hidden': 'true' }),
            m('div.home-flow__card-content', [
              m('h4', 'Spache sits in front'),
              m('p', 'It checks whether that page is already ready to serve.')
            ])
          ),
          m('div.home-flow__arrow.home-flow__arrow--two', { 'aria-hidden': 'true' }, '→'),
          m('div.home-flow__decision',
            m('div.home-flow__decision-copy', [
              m('span', '⌕'),
              m('strong', 'Cache ready?')
            ])
          ),
          m('div.home-flow__arrow.home-flow__arrow--yes', { 'aria-hidden': 'true' }, [
            m('small', 'Yes'),
            '→'
          ]),
          m('article.home-flow__card.home-flow__card--cached',
            m('i.home-flow__icon.home-flow__icon--document', { 'aria-hidden': 'true' }),
            m('div.home-flow__card-content', [
              m('h4', 'Serve the page straight away'),
              m('p', 'Send cached HTML so content is visible while your JavaScript app starts and takes over.')
            ])
          ),
          m('div.home-flow__first-visit', { 'aria-hidden': 'true' }, [
            m('small', 'Cache miss: browser visitor'),
            m('span', '↓')
          ]),
          m('article.home-flow__card.home-flow__card--origin',
            m('i.home-flow__icon.home-flow__icon--lightning', { 'aria-hidden': 'true' }),
            m('div.home-flow__card-content', [
              m('h4', 'Your SPA loads normally'),
              m('p', 'Browser visitors get the origin page while Spache prepares a rendered copy.'),
              m('p.home-flow__note', 'Recognised crawlers wait for a render on a cache miss; those renders get priority.')
            ])
          ),
          m('div.home-flow__background', [
            m('p.home-flow__background-label', 'In the background'),
            m('article.home-flow__card.home-flow__card--queue',
              m('i.home-flow__icon.home-flow__icon--servers', { 'aria-hidden': 'true' }),
              m('div.home-flow__card-content', [
                m('h4', 'Get the page ready'),
                m('p', 'Spache adds that URL to its to-do list.')
              ])
            ),
            m('div.home-flow__arrow.home-flow__arrow--queue', { 'aria-hidden': 'true' }, '→'),
            m('article.home-flow__card.home-flow__card--render',
              m('i.home-flow__icon.home-flow__icon--gear', { 'aria-hidden': 'true' }),
              m('div.home-flow__card-content', [
                m('h4', 'Build a useful page'),
                m('p', 'A browser runs your app and captures its rendered HTML and configured API responses.')
              ])
            ),
            m('div.home-flow__arrow.home-flow__arrow--render', { 'aria-hidden': 'true' }, '→'),
            m('article.home-flow__card.home-flow__card--save',
              m('i.home-flow__icon.home-flow__icon--database', { 'aria-hidden': 'true' }),
              m('div.home-flow__card-content', [
                m('h4', 'Save it for next time'),
                m('p', 'The finished page is ready whenever it is needed.')
              ])
            ),
            m('div.home-flow__arrow.home-flow__arrow--save', { 'aria-hidden': 'true' }, '→'),
            m('article.home-flow__card.home-flow__card--next',
              m('i.home-flow__icon.home-flow__icon--complete', { 'aria-hidden': 'true' }),
              m('div.home-flow__card-content', [
                m('h4', 'The next visit is ready'),
                m('p', 'Future visits can receive rendered content without waiting for the app to build it again.')
              ])
            )
          ])
        ])
      ),
      m('section.home-performance',
        m('p.home-eyebrow', 'From rendered HTML to a working app'),
        m('h3', 'Content first. Your app takes over.'),
        m('p',
          'Your app still runs in the visitor’s browser. Captured API responses can be replayed during startup, ' +
          'avoiding another fetch for the same data. Dependency checks and invalidation webhooks help keep rendered pages current.'
        )
      ),
      m('section.home-cta',
        m('h3', 'Try your site on a preview URL.'),
        m('p', 'Spache is in open beta. Create an account, add your site, and check its rendered pages on a free preview URL.'),
        m('a.link-button', { href: '/how-it-works' }, 'See how Spache works →'),
        user
          ? m('a.link-button', { href: '/sites' }, 'Go to your sites →')
          : m('button.link-button', { onclick: onSignIn }, 'Sign in to get started →')
      )
    )
  };
});

module.exports = { HomePage };
